/**
 * Active Role Context
 * Manages the currently active role for users with multiple roles
 * Allows seamless role switching without re-authentication
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './authReactContext';
import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from '../utils/localStorage.utils';

const ACTIVE_ROLE_STORAGE_KEY = 'activeRole';

/** Normalize role from API (string or object with roleCode/roleName) to a single string for comparison. */
function toRoleString(role: unknown): string | null {
  if (role == null) return null;
  if (typeof role === 'string') return role.trim() || null;
  if (typeof role === 'object') {
    const r = role as Record<string, unknown>;
    const code = r.roleCode ?? r.code;
    const name = r.roleName ?? r.name;
    if (typeof code === 'string') return code.trim() || null;
    if (typeof name === 'string') return name.trim() || null;
  }
  return null;
}

/** Normalize roles array to string[] (roleCode or roleName). */
function toRoleStrings(roles: unknown[]): string[] {
  const out: string[] = [];
  for (const r of roles) {
    const s = toRoleString(r);
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}

interface ActiveRoleContextType {
  activeRole: string | null;
  availableRoles: string[];
  setActiveRole: (role: string | null) => void;
  hasMultipleRoles: boolean;
  canSwitchRole: boolean;
}

export const ActiveRoleContext = createContext<ActiveRoleContextType | undefined>(undefined);

export const ActiveRoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();

  // Get available roles as normalized string[] (roleCode or roleName)
  const availableRoles = useMemo(() => {
    if (!user?.roles || !Array.isArray(user.roles) || user.roles.length === 0) {
      return [];
    }
    return toRoleStrings(user.roles);
  }, [user?.roles]);


  // Initialize active role: prefer value from localStorage (survives refresh); validate when we have roles
  const [activeRole, setActiveRoleState] = useState<string | null>(() => {
    const stored = getLocalStorageItem(ACTIVE_ROLE_STORAGE_KEY);
    let storedRole: string | null = null;
    if (stored) {
      try {
        storedRole = stored.startsWith('"') ? (JSON.parse(stored) as string) : stored;
      } catch {
        storedRole = stored;
      }
    }
    if (isAuthenticated && availableRoles.length > 0) {
      if (storedRole && availableRoles.includes(storedRole)) return storedRole;
      return availableRoles[0] ?? null;
    }
    return storedRole; // keep stored role in state while auth/roles load (avoids clearing on refresh)
  });

  // Update active role when user changes or roles change.
  // Only clear ACTIVE_ROLE_STORAGE_KEY when auth has finished loading and user is logged out.
  // Do not clear during initial load (isAuthLoading) or when roles are empty after refresh.
  useEffect(() => {
    if (isAuthLoading) {
      return; // wait for auth to settle; do not touch storage
    }
    if (!isAuthenticated) {
      setActiveRoleState(null);
      removeLocalStorageItem(ACTIVE_ROLE_STORAGE_KEY);
      return;
    }
    // Authenticated but roles not loaded yet (e.g. after refresh) - leave storage and state unchanged
    if (availableRoles.length === 0) {
      return;
    }

    // Restore active role from storage when roles have just loaded (e.g. after refresh)
    const stored = getLocalStorageItem(ACTIVE_ROLE_STORAGE_KEY);
    let storedRole: string | null = null;
    if (stored) {
      try {
        storedRole = stored.startsWith('"') ? (JSON.parse(stored) as string) : stored;
      } catch {
        storedRole = stored;
      }
    }

    // If current active role is not in available roles, use stored role if valid else first role
    if (activeRole && !availableRoles.includes(activeRole)) {
      const newActiveRole = storedRole && availableRoles.includes(storedRole) ? storedRole : availableRoles[0] ?? null;

      setActiveRoleState(newActiveRole);
      if (newActiveRole) {
        setLocalStorageItem(ACTIVE_ROLE_STORAGE_KEY, newActiveRole);
      } else {
        removeLocalStorageItem(ACTIVE_ROLE_STORAGE_KEY);
      }
      return;
    }

    // If no active role but user has roles, restore from storage or set to first role
    if (!activeRole && availableRoles.length > 0) {
      const newActiveRole =
        storedRole && availableRoles.includes(storedRole) ? storedRole : availableRoles[0] ?? null;
      if (newActiveRole) {
        setActiveRoleState(newActiveRole);
        setLocalStorageItem(ACTIVE_ROLE_STORAGE_KEY, newActiveRole);
      }
    }
  }, [isAuthenticated, isAuthLoading, availableRoles, activeRole]);

  // Set active role and persist to storage
  const setActiveRole = useCallback((role: string | null) => {
    if (!role) {
      setActiveRoleState(null);
      removeLocalStorageItem(ACTIVE_ROLE_STORAGE_KEY);
      return;
    }

    // Validate role is in available roles
    if (!availableRoles.includes(role)) {
      console.warn(`[ActiveRole] Attempted to set invalid role: ${role}. Available roles:`, availableRoles);
      return;
    }

    setActiveRoleState(role);
    setLocalStorageItem(ACTIVE_ROLE_STORAGE_KEY, role);

    if (import.meta.env.DEV) {
      console.log(`[ActiveRole] Role switched to: ${role}`);
    }
  }, [availableRoles]);

  const hasMultipleRoles = availableRoles.length > 1;
  const canSwitchRole = hasMultipleRoles && isAuthenticated;

  const value: ActiveRoleContextType = {
    activeRole,
    availableRoles,
    setActiveRole,
    hasMultipleRoles,
    canSwitchRole,
  };

  return (
    <ActiveRoleContext.Provider value={value}>
      {children}
    </ActiveRoleContext.Provider>
  );
};

export const useActiveRole = (): ActiveRoleContextType => {
  const context = useContext(ActiveRoleContext);
  if (context === undefined) {
    throw new Error('useActiveRole must be used within an ActiveRoleProvider');
  }
  return context;
};

/**
 * Hook to check if user has a specific role (checks active role)
 */
export const useHasRole = (role: string): boolean => {
  const { activeRole } = useActiveRole();
  const activeRoleString = activeRole != null ? String(activeRole) : null;
  return activeRoleString ? activeRoleString.toLowerCase() === role.toLowerCase() : false;
};

/**
 * Hook to check if user has any of the specified roles (checks active role)
 */
export const useHasAnyRole = (roles: string[]): boolean => {
  const { activeRole } = useActiveRole();
  if (!activeRole) return false;
  const activeRoleString = String(activeRole);
  return roles.some(role => typeof role === 'string' && activeRoleString.toLowerCase() === role.toLowerCase());
};

